import * as React from 'react';
import { observer } from 'mobx-react';

import { Selector } from 'shared/newComponents/form';
import {
  usePermissionsStore,
  useProjectsStore,
  useProjectStore,
  useRouterStore,
  useUiStore
} from 'stores';
import {
  SelectorEntities,
  SelectorType,
  SelectorTriggerSize,
  SelectorSize
} from 'shared/newEntities/components/Selector';
import { urls } from 'shared/entities/domain';
import { PlusIcon, SettingsIcon } from 'shared/newComponents/icons';
import { useModalState } from 'shared/entities/common/hooks';
import { ComponentType } from 'shared/entities/components/Component';
import { usePageTemplateMenuStore } from 'stores/PageTemplateMenuStore';

import CreateProjectModal from './CreateProjectModal';

type Props = {
  className?: string;
  triggerSize?: SelectorTriggerSize;
  onCreate?: () => void;
};

const ProjectSelector: React.FC<Props> = ({
  className,
  triggerSize,
  onCreate
}: Props) => {
  const projectsStore = useProjectsStore();
  const projectStore = useProjectStore();
  const routerStore = useRouterStore();
  const modalState = useModalState();
  const permissionsStore = usePermissionsStore();
  const uiStore = useUiStore();
  const menuStore = usePageTemplateMenuStore();

  const handleRightIconClick = React.useCallback(
    (e) => {
      e.stopPropagation();
      if (uiStore.isMobileSize) {
        uiStore.menuState.close();
      }
    },
    [uiStore.isMobileSize]
  );

  const entities = React.useMemo(() => {
    return projectsStore.projects.items.reduce<SelectorEntities>(
      (acc, project) => ({
        ...acc,
        [project.id]: {
          id: project.id,
          title: project.name,
          rightComponent: permissionsStore.canEditProject
            ? {
                Icon: SettingsIcon,
                action: {
                  type: ComponentType.link,
                  to: urls.PROJECT.tabs.SETTINGS.COMMON.create({
                    projectId: project.id
                  }),
                  onClick: handleRightIconClick
                },
                reference: (t) =>
                  t('ProjectSelector.settings', {
                    ns: 'newContainers'
                  })
              }
            : undefined
        }
      }),
      {} as SelectorEntities
    );
  }, [
    projectsStore.projects.items,
    projectStore.entity?.id,
    permissionsStore.canEditProject
  ]);

  const selectProject = React.useCallback(
    (projectId: string | null) => {
      if (!projectId) {
        return;
      }
      if (uiStore.isMobileSize) {
        uiStore.menuState.close();
      }

      if (
        menuStore.activeSection &&
        menuStore.activeSection.action.type === ComponentType.link &&
        projectStore.projectId
      ) {
        const curPathName = menuStore.activeSection.action.to;
        const newPath = curPathName.replace(projectStore.projectId, projectId);
        routerStore.push(newPath);
        return;
      }

      // если на странице настроек кабинета или профиля, меняем проект
      if (routerStore.matchPath(urls.SETTINGS.mask)) {
        projectStore.reset();
        projectStore.load(projectId);
        return;
      }

      routerStore.push(
        urls.PROJECT.tabs.ONBOARDING.create({
          projectId
        })
      );
    },
    [uiStore.isMobileSize, menuStore.activeSection, projectStore.projectId]
  );

  const handleCreate = React.useCallback(() => {
    onCreate?.();
    modalState.close();
  }, [modalState, onCreate]);

  return (
    <>
      {modalState.opened && (
        <CreateProjectModal opened={modalState.opened} onClose={handleCreate} />
      )}
      <Selector
        topItem={
          permissionsStore.canCreateProject
            ? {
                Icon: PlusIcon,
                title: (t) =>
                  t('ProjectSelector.topItem.title', {
                    ns: 'newContainers'
                  }),
                onClick: modalState.open
              }
            : undefined
        }
        className={className}
        type={SelectorType.single}
        value={projectStore.projectId}
        onChange={selectProject}
        placeholder={(t) =>
          t('ProjectSelector.placeholder', {
            ns: 'newContainers'
          })
        }
        label={(t) =>
          t('ProjectSelector.label', {
            ns: 'newContainers'
          })
        }
        withRemoving={false}
        loadingStage={projectsStore.projects.loadingStage}
        options={{
          entities,
          keys: projectsStore.projects.keys
        }}
        size={SelectorSize.small}
        triggerSize={triggerSize}
      />
    </>
  );
};

export default observer(ProjectSelector);
